import { type } from '@ngrx/signals';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  inject,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { catchError, map, Observable, Subject, takeUntil, throwError } from 'rxjs';
import { Table, TableModule, TableService } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { FilterService, MessageService } from 'primeng/api';
import { Column, Paging } from '../../core/types/helper.model';
import { CustomTableService } from '../../core/auth/services/table.service';
import { normalizeDate } from '../../core/utils/helper.functions';
import { Button } from 'primeng/button';
import { ApiService } from '../../core/auth/services/api.service';
import { HttpHeaders } from '@angular/common/http';
import * as FileSaver from 'file-saver';

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [CommonModule, TableModule, Button],
  templateUrl: './table.component.html',
  styleUrl: './table.component.css',
  providers: [TableService, FilterService],
})
export class TableComponent {
  @Input({ required: true }) columns: Column[] = [];
  @Input({ required: true }) getDataFunc!: (
    data: Paging,
    componentInstance?: any,
  ) => Observable<any>;
  @Input() componentInstance?: any;
  @Input() totalItems = 0;
  @Input() paginationPageSize = 10;
  @Input() customFilters: Record<string, any> = {};
  @Input() showExportToExcel = false;

  @ContentChild('customFilterTemplate') customFilterTemplate!: TemplateRef<any>;

  @ViewChild(Table) dataTable!: Table;
  data: any[] = [];
  loading = false;
  currentPageRows: any[] = [];
  rowsPerPageOptions = [5, 10, 50, 100, 200];
  sortField: string | null = null;
  sortOrder: number | null = null;
  rowsForTable = 10;
  @Input() exportUrl!: string;
  apiService: ApiService = inject(ApiService);

  private _destroy$ = new Subject<void>();
  previousFilter: string | undefined;

  constructor(
    private cd: ChangeDetectorRef,
    private messageService: MessageService,
    private sanitizer: DomSanitizer,
    private _tableService: CustomTableService,
  ) {}

  ngOnInit(): void {
    this._tableService.refreshTable$.pipe(takeUntil(this._destroy$)).subscribe((refreshTable) => {
      if (refreshTable) {
        const currentState = {
          first: this.dataTable.first || 0,
          rows: this.dataTable.rows,
          sortField: this.sortField,
          sortOrder: this.sortOrder,
          filters: this.dataTable.filters,
        };
        this.loadData(currentState);
        this._tableService.setrefreshTable(false);
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  resetFilters(): void {
    if (this.dataTable) {
      this.dataTable.reset();
    }
  }

  loadData(event: any): void {
    this.data = [];
    this.loading = true;
    this.cd.detectChanges();

    const rows = event.rows ?? this.paginationPageSize;
    const pageNumber = (event.first || 0) / rows + 1;

    if (event.rows && event.rows !== this.rowsForTable) {
      Promise.resolve().then(() => (this.rowsForTable = event.rows));
    }

    this.sortField = event.sortField ?? this.sortField;
    this.sortOrder = event.sortOrder ?? this.sortOrder;

    const filterString = this.buildFilterString(event.filters);

    const requestParams: Paging = {
      pageSize: rows,
      pageNumber,
      filter: filterString,
      orderby: this.getSortQuery(this.sortField, this.sortOrder),
    };

    this.previousFilter = requestParams.filter;

    this.getDataFunc(requestParams, this.componentInstance).subscribe({
      next: (res: any) => {
        this.data = res.data ?? [];
        this.totalItems = res.paging?.totalItems ?? 0;
        this.currentPageRows = this.data;
        this.loading = false;
      },
      error: (err: any) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err.error?.message ?? 'Failed to load data',
        });
      },
    });
  }

  // private buildFilterString(filters: any): string {
  //   // Merge customFilters from parent with table filters
  //   const mergedFilters = { ...this.customFilters, ...(filters || {}) };

  //   if (!mergedFilters || Object.keys(mergedFilters).length === 0) return `status eq 'Active'`;

  //   const clauses: string[] = [];

  //   Object.entries(mergedFilters).forEach(([key, filterArray]: any) => {
  //     if (!Array.isArray(filterArray) || !filterArray[0]?.value) return;

  //     const { value, matchMode } = filterArray[0];

  //     const clauseMap: Record<string, string> = {
  //       contains: `contains(${key}, '${value}')`,
  //       equals: `${key} eq '${value}'`,
  //       eq: `${key} eq '${value}'`,
  //       startsWith: `startswith(${key}, '${value}')`,
  //       endsWith: `endswith(${key}, '${value}')`,
  //     };
  //     if (matchMode === 'dateIs') {
  //       clauses.push(`${key} eq ${normalizeDate(new Date(value))}`);
  //     }

  //     const clause = clauseMap[matchMode];
  //     if (clause) clauses.push(`${clause}`);
  //   });

  //   return clauses.length ? clauses.join(' and ') : '';
  // }

  private buildFilterString(filters: any): string {
    const mergedFilters = { ...this.customFilters, ...(filters || {}) };

    if (!mergedFilters || Object.keys(mergedFilters).length === 0) {
      return `status eq 'Active'`;
    }

    const clauses: string[] = [];

    Object.entries(mergedFilters).forEach(([key, filterArray]: any) => {
      if (!Array.isArray(filterArray) || !filterArray[0]?.value) return;

      const { value, matchMode } = filterArray[0];

      const column = this.getColumn(key);
      const type = column?.filterType ?? 'text'; // DEFAULT = text

      const clause = this.buildClause(key, value, matchMode, type);

      if (clause) clauses.push(clause);
    });

    return clauses.length ? clauses.join(' and ') : '';
  }
  private getColumn(field: string): Column | undefined {
    return this.columns.find((c) => c.field === field);
  }

  private buildClause(
    key: string,
    value: any,
    matchMode: string,
    type: 'text' | 'numeric' | 'date' | 'custom',
  ): string {
    const strValue = String(value).replace(/'/g, "''");

    const isNumber = type === 'numeric';
    const isDate = type === 'date';

    // ---------------- CUSTOM TYPE ----------------
    // You can handle backend-specific logic here
    if (type === 'custom') {
      return `${key} eq ${value}`;
    }

    switch (matchMode) {
      // ---------------- TEXT ----------------
      case 'contains':
        return isNumber || isDate ? `${key} eq ${value}` : `contains(tolower(${key}), '${strValue.toLowerCase()}')`;

      case 'equals':
      case 'eq':
        return isNumber || isDate ? `${key} eq ${value}` : `${key} eq '${strValue}'`;

      case 'startsWith':
        return isNumber || isDate ? `${key} eq ${value}` : `startswith(tolower(${key}), '${strValue.toLowerCase()}')`;

      case 'endsWith':
        return isNumber || isDate ? `${key} eq ${value}` : `endswith(tolower(${key}), '${strValue.toLowerCase()}')`;

      // ---------------- NUMBER ----------------
      case 'greaterThan':
        return `${key} gt ${value}`;

      case 'lessThan':
        return `${key} lt ${value}`;

      // ---------------- DATE ----------------
      case 'dateIs':
        return `${key} eq ${normalizeDate(new Date(value))}`;

      default:
        return '';
    }
  }
  private getSortQuery(sortField: string | null, sortOrder: number | null): string {
    if (!sortField || sortOrder == null) return '';
    return `${sortField} ${sortOrder === 1 ? 'asc' : 'desc'}`;
  }

  renderCell(col: Column, rowData: any): SafeHtml {
    try {
      const html: unknown = col.cellRenderer ? col.cellRenderer(rowData) : '';
      return this.sanitizer.bypassSecurityTrustHtml(String(html ?? ''));
    } catch {
      return '';
    }
  }

  handleCellClick(col: Column, rowData: any): void {
    if (typeof col.onClick === 'function') {
      col.onClick(rowData);
    }
  }

  renderCustomFilter(col: Column): TemplateRef<any> | null {
    return col.customFilterTemplate || this.customFilterTemplate || null;
  }
  onExport(): void {
    if (this.showExportToExcel && this.exportUrl?.length > 0) {
      const headers = new HttpHeaders().set('Accept', 'application/octet-stream');

      // Prepare query params with previous filter and exportToExcel flag
      const params: any = {};
      if (this.previousFilter) {
        params['filter'] = this.previousFilter;
      }
      params['exportToExcel'] = true;
      this.loading = true;

      this.apiService
        .get(this.exportUrl, {
          params,
          headers,
          responseType: 'blob',
        } as any)
        .pipe(
          map((data: any) => {
            if (data) {
              const newFileName = `Data ${new Date().toISOString().slice(0, 10)}.xlsx`;
              FileSaver.saveAs(data, newFileName);
              this.loading = false;
            }
            return data;
          }),
          catchError((error: any) => {
            this.loading = false;
            this.messageService.add({
              key: 'custom-toast',
              severity: 'error',
              summary: 'Error',
              detail: error?.error?.message ?? 'Failed to export data',
            });
            return throwError(() => error);
          }),
        )
        .subscribe();
    }
  }

  public reload(): void {
    if (this.dataTable) {
      const currentState = {
        first: this.dataTable.first || 0,
        rows: this.dataTable.rows || this.paginationPageSize,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
        filters: this.dataTable.filters,
      };
      this.loadData(currentState);
    }
  }
}
